// Część 2 hero 47-A „GENERATOR FANOWSKICH EDITÓW” (paczka H47): nagłówek H2 i trzy kroki właściciela z grafiką.
//
// Krok 01: pigułka utworu (ta sama co u nasady paraboli, fala 32 słupki). Krok 02: siatka unikatowych editów
// (8 × 3 od 1336 px, 6 × 3 niżej; 16 stylów, sceny i trzy prawdziwe edity jako MAŁE wideo 180 × 320). Do końca
// sekwencji komórka wideo pokazuje sam plakat (żadnego <video>, więc nic się nie pobiera); po sekwencji gra przez
// `ds/media/Media` (jedyna droga do <video> poza odtwarzaczem, bramka `one-player-gate`). Krok 03: sześć kont
// z nickami świata D (dane przykładowe jako stała w kodzie, jak w `LandingEditShowcase.tsx`), nicki od 1336 px.
//
// Ruch (`useGeneratorSequence`): nagłówek i tytuły kroków wjeżdżają z maski, utwór wjeżdża do generatora, fala
// rozpada się iskrami na siatkę, edity lecą na konta; gra raz, po wejściu 30 % kroków. Warstwa ruchu
// (`.mp-hg-war`) leży nad krokami i jest dla czytnika JEDNYM obrazem (`role="img"` + `landing.hero.generatorAlt`)
// po liście kroków; grafiki kroków są `aria-hidden`, więc czytnik słyszy nagłówek, trzy tytuły i jeden opis.
// Na telefonie bez stałych łączników między krokami (przecinałyby tytuły 02 i 03, MAPA §9.7).

import { useCallback, useRef, useState } from 'react'
import { useI18n } from '../../lib/i18n'
import { Media } from '../../ds/media/Media'
import { FALA32, NAZWY_KONT, kadrySiatki } from '../../lib/landingParabola'
import type { KomorkaSiatki } from '../../lib/landingParabola'
import { useGeneratorSequence } from '../../hooks/useGeneratorSequence'
import { Account, TrackPill, kadrSrc } from './HeroParabola'
import '../../styles/landing-parabola.css'

const STEPS = ['landing.hero.step1', 'landing.hero.step2', 'landing.hero.step3'] as const

/**
 * Siatka unikatowych editów jednej orientacji. `videos`: sekwencja dobiegła końca, trzy komórki grają małe
 * wideo (wyciszone, w pętli); wcześniej stoi plakat. `snap`: kopia do statycznego HTML (prerender).
 */
export function GeneratorGrid({ wide, videos = false, snap = false }: { wide: boolean; videos?: boolean; snap?: boolean }) {
  const cells: KomorkaSiatki[] = kadrySiatki(wide ? 24 : 18)
  return (
    <div className={`mp-hg-siat is-${wide ? 'h' : 'v'}${snap ? ' mp-hg-snap' : ''}`}>
      {cells.map((q, i) => ('v' in q ? (
        <span key={i} className="mp-hg-ka" data-i={i} data-video={q.v} style={{ backgroundImage: `url(${q.v}.jpg)` }}>
          {videos ? <Media video src={`${q.v}.mp4`} poster={`${q.v}.jpg`} ratio="9:16" /> : null}
        </span>
      ) : (
        <span key={i} className="mp-hg-ka" data-i={i} style={{ backgroundImage: `url(${kadrSrc(q.k)})` }} />
      )))}
    </div>
  )
}

export default function HeroGenerator({ wide, motion }: { wide: boolean; motion: boolean }) {
  const { t } = useI18n()
  const sec = useRef<HTMLElement>(null)
  const [videos, setVideos] = useState(false)
  useGeneratorSequence(sec, wide, motion, useCallback(() => setVideos(true), []))
  // Dwa wiersze nagłówka w masce (na telefonie jeden pod drugim); „\n” w słowniku wyznacza podział.
  const [line1, line2 = ''] = t('landing.hero.generatorTitle').split('\n')
  const kom = wide ? 24 : 18
  return (
    <section className="mp-hg" aria-labelledby="landing-generator-heading" ref={sec}>
      <h2 id="landing-generator-heading">
        <span className="mp-hg-mk" data-i="0"><span>{line1}</span></span>
        {line2 ? <>{' '}<span className="mp-hg-mk" data-i="1"><span>{line2}</span></span></> : null}
      </h2>
      <ol className="mp-hg-kroki">
        {STEPS.map((key, i) => (
          <li key={key} className="mp-hg-kr" data-i={i}>
            <div className="mp-hg-kt">
              <div><span className="mp-hg-no" aria-hidden="true">{`0${i + 1}`}</span><h3>{t(key)}</h3></div>
            </div>
            <div className="mp-hg-kv" aria-hidden="true">
              {i === 0 ? <div className="mp-hg-ut"><TrackPill name={t('landing.hero.map.track')} bars={FALA32} /></div> : null}
              {i === 1 ? <GeneratorGrid key={String(wide)} wide={wide} videos={videos} /> : null}
              {i === 2 ? (
                <div className="mp-hg-kon">
                  {NAZWY_KONT.map(([nick, pf, av], j) => (
                    <span key={nick} className="mp-hg-kn" data-i={j}>
                      <Account e={((j % 3) + 1) as 1 | 2 | 3} d={44} pf={pf} av={av} />
                      <span className="mp-hg-nm"><b>{nick}</b></span>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
      {/* Warstwa ruchu: położenie z pomiaru układu (useGeneratorSequence), w spoczynku widać tylko łączniki 1440. */}
      <div className="mp-hg-war" role="img" aria-label={t('landing.hero.generatorAlt')}>
        {wide ? [0, 1, 2].map((i) => <i key={`a${i}`} className="mp-hg-ln gr" data-g="a" data-i={i} />) : null}
        {wide ? NAZWY_KONT.map((_, i) => <i key={`b${i}`} className="mp-hg-ln" data-g="b" data-i={i} />) : null}
        {Array.from({ length: kom }, (_, j) => <i key={`s${j}`} className="mp-hg-isk" data-j={j} />)}
        {NAZWY_KONT.map((_, j) => <i key={`l${j}`} className="mp-hg-lot" data-j={j} />)}
      </div>
    </section>
  )
}
